import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DayPicker, Matcher } from 'react-day-picker'
import { enUS, ro } from 'react-day-picker/locale'
import { startOfToday } from 'date-fns'

import Icon from '../ui/Icon'
import { displayShortDayOfWeek, displayShortMonth } from '../../utils/dateUtils'

import 'react-day-picker/dist/style.css'

type DayPickCardProps = {
  /** The selected day when it is not one of the strip's days; the card then shows it instead of "Select". */
  pickedDay?: Date
  disablePastDates?: boolean
  onSelect: (day: Date) => void
  /** When set, picking the picked date again unpicks it. */
  onClear?: () => void
}

/** First card of the day strip: opens a calendar to pick any day beyond the strip. */
const DayPickCard = ({ pickedDay, disablePastDates = false, onSelect, onClear }: DayPickCardProps) => {
  const { t, i18n } = useTranslation(['bookings', 'inputs'])
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const language = i18n.resolvedLanguage ?? i18n.language
  const today = startOfToday()
  const disabled: Matcher | undefined = disablePastDates ? { before: today } : undefined

  useEffect(() => {
    if (!open) {
      return
    }
    const close = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  return (
    <div ref={wrapperRef} className="input-date day-pick">
      <button
        type="button"
        className={`day-pill day-pill-pick ${pickedDay ? 'on' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
      >
        {pickedDay ? (
          <>
            <span className="day">{displayShortDayOfWeek(pickedDay, language)}</span>
            <span className="date">{pickedDay.getDate()}</span>
            <span className="indicator">{displayShortMonth(pickedDay, language).toLowerCase()}</span>
          </>
        ) : (
          <>
            <span className="day">{t('bookings:booking.day-list.date')}</span>
            <span className="date">
              <Icon name="calendar" size={20} />
            </span>
            <span className="indicator">{t('bookings:booking.day-list.select').toLowerCase()}</span>
          </>
        )}
      </button>
      {open && (
        <div className="input-date-picker" role="dialog" aria-label={t('inputs:select-date')}>
          <DayPicker
            mode="single"
            locale={language.startsWith('ro') ? ro : enUS}
            selected={pickedDay}
            defaultMonth={pickedDay ?? today}
            endMonth={new Date(today.getFullYear() + 10, 11, 31)}
            disabled={disabled}
            onSelect={day => {
              if (day) {
                setOpen(false)
                onSelect(day)
              } else if (onClear) {
                setOpen(false)
                onClear()
              }
            }}
            showOutsideDays
            captionLayout="dropdown"
            navLayout="around"
          />
        </div>
      )}
    </div>
  )
}

export default DayPickCard
